/**
 * WAF Access Control — per-subdomain allow/deny lists. Two top-level tabs,
 * Whitelist (allowlist) and Blacklist (blocklist); inside each a segmented
 * IP/CIDR | Geo sub-tab. That's the four backend lists (`ip|geo` × `white|black`).
 * Each entry is add/delete-only — paste IPs/CIDRs (one per line) or pick
 * countries; delete per-row or in bulk across the selected rows.
 */
import { useEffect, useMemo, useState } from 'react';
import { ShieldBan, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  Button,
  IconButton,
  Input,
  Textarea,
  Skeleton,
  Tooltip,
  TooltipProvider,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafAccessControl,
  useAddWafAccessControl,
  useDeleteWafAccessControl,
  wafExtractError,
  type WafAccessControlEntry,
  type WafAcKind,
  type WafAcList,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import { loadJSON, saveJSON } from '../analytics/storage';
import { MultiSelect, type MultiSelectOption } from '../_shared/MultiSelect';
import { geoCountries, humanizeGeoName, countryName, acGeoCode, acGeoCountry } from '../_shared/geo';
import styles from './WafAccessControl.module.css';

const LIST_TABS: { value: WafAcList; label: string }[] = [
  { value: 'white', label: 'Whitelist' },
  { value: 'black', label: 'Blacklist' },
];

const KIND_TABS: { value: WafAcKind; label: string }[] = [
  { value: 'ip', label: 'IP / CIDR' },
  { value: 'geo', label: 'Geo' },
];

const PAGE_SIZE = 20;

/** Country options for the geo picker (value = "{continent}-{country}"). */
const GEO_OPTIONS: MultiSelectOption[] = geoCountries
  .map((c) => ({ value: acGeoCode(c), label: humanizeGeoName(c.name) }))
  .sort((a, b) => a.label.localeCompare(b.label));

/** Add dialog — IP/CIDR textarea (one per line) or a country multi-select. */
function AddEntryDialog({
  serviceId,
  domain,
  kind,
  list,
  open,
  onOpenChange,
}: {
  serviceId: string;
  domain: string;
  kind: WafAcKind;
  list: WafAcList;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { show } = useToast();
  const [raw, setRaw] = useState('');
  const [geo, setGeo] = useState<string[]>([]);

  // Reset whenever the dialog (re)opens.
  useEffect(() => {
    if (open) {
      setRaw('');
      setGeo([]);
    }
  }, [open]);

  const add = useAddWafAccessControl(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Entries added' }); trackFeatureAction('waf', 'access_control', 'create'); onOpenChange(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Add failed') }),
  });

  const ipValues = useMemo(
    () => Array.from(new Set(raw.split('\n').map((s) => s.trim()).filter(Boolean))),
    [raw],
  );
  const values = kind === 'ip' ? ipValues : geo;
  const valid = values.length > 0;

  const submit = () => {
    if (!valid) return;
    add.mutate({ domain, kind, list, values });
  };

  const listLabel = list === 'white' ? 'whitelist' : 'blacklist';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{kind === 'ip' ? 'Add IP / CIDR' : 'Add countries'}</DialogTitle>
          <DialogDescription>
            Add to the <strong>{kind === 'ip' ? 'IP / CIDR' : 'geo'}</strong> {listLabel} for{' '}
            <strong>{domain}</strong>.
          </DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {kind === 'ip' ? (
            <>
              <Textarea
                rows={6}
                autoFocus
                placeholder={'203.0.113.4\n198.51.100.0/24\n2001:db8::/32'}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                disabled={add.isPending}
                style={{ fontFamily: 'var(--vn-font-mono)' }}
              />
              <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
                One IP or CIDR per line — {ipValues.length} to add.
              </span>
            </>
          ) : (
            <>
              <MultiSelect
                placeholder="Select countries…"
                options={GEO_OPTIONS}
                value={geo}
                onChange={setGeo}
                disabled={add.isPending}
              />
              <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
                {geo.length} countr{geo.length === 1 ? 'y' : 'ies'} to add.
              </span>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={add.isPending || !valid}>
            Add{values.length ? ` ${values.length}` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Per-row delete — IconButton + confirm. */
function DeleteEntryCell({
  serviceId,
  domain,
  entry,
}: {
  serviceId: string;
  domain: string;
  entry: WafAccessControlEntry;
}) {
  const { show } = useToast();
  const [confirm, setConfirm] = useState(false);
  const del = useDeleteWafAccessControl(serviceId, {
    onSuccess: () => { setConfirm(false); show({ tone: 'success', title: 'Entry deleted' }); trackFeatureAction('waf', 'access_control', 'delete'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <TooltipProvider>
        <Tooltip content="Delete" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Delete entry" onClick={() => setConfirm(true)}>
            <Trash2 size={14} />
          </IconButton>
        </Tooltip>
      </TooltipProvider>
      {confirm && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirm(false); }}
          title="Delete entry?"
          description={`Remove "${entry.value}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ domain, kind: entry.kind, list: entry.list, ids: [entry.id] })}
        />
      )}
    </div>
  );
}

/** Tabs + toolbar + bulk bar + table for a resolved domain. */
function AccessControlBody({ serviceId, domain }: { serviceId: string; domain: string }) {
  const { show } = useToast();
  const listKey = `waf:${serviceId}:ac:list`;
  const kindKey = `waf:${serviceId}:ac:kind`;
  const [list, setList] = useState<WafAcList>(() => loadJSON<WafAcList>(listKey, 'white'));
  const [kind, setKind] = useState<WafAcKind>(() => loadJSON<WafAcKind>(kindKey, 'ip'));
  const [search, setSearch] = useState('');
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const q = useWafAccessControl(serviceId, domain, kind, list, { page, pageSize: PAGE_SIZE });
  const entries = useMemo(() => q.data?.entries ?? [], [q.data]);
  const total = q.data?.pagination?.total ?? entries.length;
  const pageCount = Math.max(1, q.data?.pagination?.total_pages ?? 1);

  const changeList = (v: WafAcList) => { setList(v); saveJSON(listKey, v); setPage(1); setRowSelection({}); };
  const changeKind = (v: WafAcKind) => { setKind(v); saveJSON(kindKey, v); setPage(1); setRowSelection({}); };
  // Selection is page-scoped (server pagination) — clear it when the page/domain changes.
  useEffect(() => setRowSelection({}), [page, domain]);

  // Client-side search over the current page only.
  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return entries;
    return entries.filter(
      (e) =>
        e.value.toLowerCase().includes(s) ||
        (e.kind === 'geo' && countryName(acGeoCountry(e.value)).toLowerCase().includes(s)),
    );
  }, [entries, search]);

  const selectedIds = useMemo(
    () => Object.entries(rowSelection).filter(([, v]) => v).map(([k]) => Number(k)),
    [rowSelection],
  );

  const del = useDeleteWafAccessControl(serviceId, {
    onSuccess: () => { setRowSelection({}); setConfirmBulk(false); show({ tone: 'success', title: 'Entries deleted' }); trackFeatureAction('waf', 'access_control', 'delete'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Bulk delete failed') }),
  });

  const columns = useMemo<ColumnDef<WafAccessControlEntry, unknown>[]>(
    () => [
      {
        id: 'value',
        accessorKey: 'value',
        header: kind === 'ip' ? 'IP / CIDR' : 'Country',
        enableSorting: false,
        cell: ({ row }) => {
          const e = row.original;
          if (e.kind === 'geo') {
            return (
              <div className={styles.geoCell}>
                <span>{countryName(acGeoCountry(e.value))}</span>
                <span className={styles.codeSub}>{e.value}</span>
              </div>
            );
          }
          return <span className={styles.idCell}>{e.value}</span>;
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => <DeleteEntryCell serviceId={serviceId} domain={domain} entry={row.original} />,
      },
    ],
    [serviceId, domain, kind],
  );

  return (
    <div>
      <Tabs value={list} onValueChange={(v) => changeList(v as WafAcList)}>
        <TabsList>
          {LIST_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Tabs className={styles.kindTabs} value={kind} onValueChange={(v) => changeKind(v as WafAcKind)}>
        <TabsList>
          {KIND_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className={styles.toolbar}>
        <Input
          className={styles.search}
          placeholder={kind === 'ip' ? 'Search IP / CIDR…' : 'Search country…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className={styles.spacer} />
        <Button
          variant="secondary"
          startIcon={<RefreshCw size={15} className={q.isFetching ? styles.spin : undefined} />}
          disabled={q.isFetching}
          onClick={() => q.refetch()}
        >
          Refresh
        </Button>
        <Button startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
          {kind === 'ip' ? 'Add IP / CIDR' : 'Add countries'}
        </Button>
      </div>

      {selectedIds.length > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedIds.length} selected</span>
          <span className={styles.bulkSpacer} />
          <Button size="sm" variant="danger" disabled={del.isPending} onClick={() => setConfirmBulk(true)}>
            Delete selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRowSelection({})}>Clear</Button>
        </div>
      )}

      {q.isPending ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} style={{ height: 44 }} />)}
        </div>
      ) : q.isError ? (
        <Alert tone="danger" title="Couldn't load access control" actions={<button onClick={() => q.refetch()}>Retry</button>}>
          {wafExtractError(q.error, "This domain's access-control entries failed to load.")}
        </Alert>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={visible}
            getRowId={(r) => String(r.id)}
            enableSelection
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            emptyState={
              search
                ? 'No entries match your search.'
                : `No ${kind === 'ip' ? 'IP / CIDR' : 'geo'} entries in this ${list === 'white' ? 'whitelist' : 'blacklist'}.`
            }
            footer={`Showing ${visible.length} of ${total} entr${total === 1 ? 'y' : 'ies'}`}
          />
          {pageCount > 1 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--vn-space-4)' }}>
              <Pagination
                page={page}
                pageCount={pageCount}
                onPageChange={setPage}
                summary={`Page ${page} of ${pageCount}`}
              />
            </div>
          )}
        </>
      )}

      {addOpen && (
        <AddEntryDialog
          serviceId={serviceId}
          domain={domain}
          kind={kind}
          list={list}
          open={addOpen}
          onOpenChange={setAddOpen}
        />
      )}

      {confirmBulk && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmBulk(false); }}
          title={`Delete ${selectedIds.length} entr${selectedIds.length === 1 ? 'y' : 'ies'}?`}
          description="The selected entries will be removed. This cannot be undone."
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ domain, kind, list, ids: selectedIds })}
        />
      )}
    </div>
  );
}

export function WafAccessControl({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<ShieldBan size={22} />}
        title="Access Control"
        description="Allow or deny traffic to the selected subdomain by source IP/CIDR or country — manage the whitelist and blacklist."
      />

      <div className={styles.scopeBar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<ShieldBan size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to manage access control for. Proxy a record first."
        />
      ) : !domain ? (
        <EmptyState
          icon={<ShieldBan size={20} />}
          title="Select a subdomain"
          description="Pick a domain and subdomain above to manage its access-control lists."
        />
      ) : (
        <AccessControlBody key={domain} serviceId={serviceId} domain={domain} />
      )}
    </Container>
  );
}

export default WafAccessControl;
